import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BOOKING_TIME_ZONE,
  type CreateInspectionAvailabilityDto,
  type FileReinspectionDto,
  type InspectionAvailabilityDto,
  type OpenReinspectionDto,
  type RecordReinspectionOutcomeDto,
  type ReinspectionBookingDto,
  type ReinspectionSlotDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * Re-inspection booking: the officer's availability windows, their upcoming
 * appointments, and the re-inspection on a file. Every write is gated
 * server-side (the municipality's switch and the file's own scope), so these
 * hooks only decide what to show.
 */
export interface InspectionBookingMe {
  enabledLocalGovernmentIds: string[];
  availability: InspectionAvailabilityDto[];
  upcoming: ReinspectionBookingDto[];
}

export const bookingKeys = {
  all: ['inspection-booking'] as const,
  me: ['inspection-booking', 'me'] as const,
  file: (fileId: string) => ['inspection-booking', 'file', fileId] as const,
  slots: (fileId: string) => ['inspection-booking', 'slots', fileId] as const,
};

export function useInspectionBookingMe(enabled = true) {
  return useQuery({
    queryKey: bookingKeys.me,
    queryFn: async () => (await api.get<InspectionBookingMe>('/inspection-booking/me')).data,
    enabled,
    staleTime: 60_000,
  });
}

export function useCreateAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateInspectionAvailabilityDto) =>
      (await api.post<InspectionAvailabilityDto>('/inspection-booking/availability', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.all }),
  });
}

export function useDeleteAvailability() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/inspection-booking/availability/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.all }),
  });
}

export function useFileReinspection(fileId: string) {
  return useQuery({
    queryKey: bookingKeys.file(fileId),
    queryFn: async () => (await api.get<FileReinspectionDto>(`/files/${fileId}/reinspection`)).data,
  });
}

export function useReinspectionSlots(fileId: string, enabled: boolean) {
  return useQuery({
    queryKey: bookingKeys.slots(fileId),
    queryFn: async () =>
      (await api.get<ReinspectionSlotDto[]>(`/files/${fileId}/reinspection/slots`)).data,
    enabled,
  });
}

function useFileAction<T>(action: (body: T) => Promise<ReinspectionBookingDto>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingKeys.all }),
  });
}

export function useOpenReinspection(fileId: string) {
  return useFileAction(
    async (body: OpenReinspectionDto) =>
      (await api.post<ReinspectionBookingDto>(`/files/${fileId}/reinspection`, body)).data,
  );
}

export function useBookReinspection(fileId: string) {
  return useFileAction(
    async (startsAt: string) =>
      (await api.post<ReinspectionBookingDto>(`/files/${fileId}/reinspection/book`, { startsAt }))
        .data,
  );
}

export function useCancelReinspection(fileId: string) {
  return useFileAction<void>(
    async () =>
      (await api.post<ReinspectionBookingDto>(`/files/${fileId}/reinspection/cancel`, {})).data,
  );
}

export function useWithdrawReinspection(fileId: string) {
  return useFileAction<void>(
    async () =>
      (await api.post<ReinspectionBookingDto>(`/files/${fileId}/reinspection/withdraw`, {})).data,
  );
}

export function useRecordReinspectionOutcome(fileId: string) {
  return useFileAction(
    async (body: RecordReinspectionOutcomeDto) =>
      (await api.post<ReinspectionBookingDto>(`/files/${fileId}/reinspection/outcome`, body)).data,
  );
}

// --- Formatting -------------------------------------------------------------

function locale(language: string): string {
  return language.startsWith('fr') ? 'fr-CA' : 'en-CA';
}

/** "Mon, Oct 26, 9:00 a.m." in America/Moncton, whatever the browser's zone. */
export function formatSlot(iso: string, language: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale(language), {
      timeZone: BOOKING_TIME_ZONE,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso)),
  );
}

/** Just the time of a slot, in America/Moncton. */
export function formatTime(iso: string, language: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale(language), {
      timeZone: BOOKING_TIME_ZONE,
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(iso)),
  );
}

/** A `YYYY-MM-DD` calendar date, formatted without shifting a day. */
export function formatDay(date: string, language: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale(language), {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(new Date(Date.UTC(y, m - 1, d))),
  );
}

/** Minutes after midnight as `HH:MM`, for a time input. */
export function minutesToClock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** `HH:MM` to minutes after midnight; null when malformed. */
export function clockToMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 24 || m > 59 || (h === 24 && m > 0)) return null;
  return h * 60 + m;
}

/** Group slots by their Moncton calendar day, keeping order. */
export function groupSlotsByDay(
  slots: ReinspectionSlotDto[],
): Array<[string, ReinspectionSlotDto[]]> {
  const map = new Map<string, ReinspectionSlotDto[]>();
  for (const s of slots) {
    const list = map.get(s.date) ?? [];
    list.push(s);
    map.set(s.date, list);
  }
  return [...map.entries()];
}

/** `YYYY-MM-DD` `days` after `date`. */
export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
